import { useState } from 'react';
import NextImage from 'next/image';
import { Card, Stack, Typography } from '@krakentech/coral';
import styled from 'styled-components';

import { HappyFaceIcon, MehFaceIcon, SadFaceIcon } from '@/public/images';

type FeedbackType = 'happy' | 'meh' | 'sad';

const Feedback = () => {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(
    null
  );

  const handleFeedback = (feedbackType: FeedbackType) => {
    // Only allow setting feedback if no feedback has been selected yet
    if (selectedFeedback === null) {
      setSelectedFeedback(feedbackType);
      // TODO: Fire FE analytics event and request questions from backend
      // redirect to feedback pages.
    }
  };

  const isDisabled = !!selectedFeedback; // All buttons are disabled from clicks once any feedback is selected

  const isVisuallyDisabled = (type: FeedbackType) =>
    Boolean(selectedFeedback && selectedFeedback !== type); // Only non-selected buttons look disabled

  return (
    <Card fullWidth>
      <Stack direction="vertical" gap="smMd" alignItems="center">
        <Typography variant="h2" textAlign="center">
          How did we do today?
        </Typography>
        <Stack gap="smMd" justifyContent="center">
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('sad')}
            aria-label="Sad"
            selected={selectedFeedback === 'sad'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('sad')}
          >
            <NextImage
              src={SadFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('meh')}
            aria-label="Neutral"
            selected={selectedFeedback === 'meh'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('meh')}
          >
            <NextImage
              src={MehFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('happy')}
            aria-label="Happy"
            selected={selectedFeedback === 'happy'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('happy')}
          >
            <NextImage
              src={HappyFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
        </Stack>
      </Stack>
    </Card>
  );
};

export default Feedback;

const StyledIconButton = styled.button<{
  selected: boolean;
  disabled?: boolean;
  visuallyDisabled: boolean;
}>`
  border-radius: 50%;
  padding: 0;
  min-width: 60px;
  min-height: 60px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid
    ${({ selected, visuallyDisabled }) => {
      if (visuallyDisabled) return '#e0e0e0';
      return selected ? '#1976d2' : 'transparent';
    }};
  background: ${({ selected, visuallyDisabled }) => {
    if (visuallyDisabled) return '#f5f5f5';
    return selected ? 'black' : 'transparent';
  }};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition:
    border 0.2s,
    background 0.2s;
  opacity: ${({ visuallyDisabled }) => (visuallyDisabled ? 0.5 : 1)};
`;
